import { FC, useState, useEffect, useRef } from 'react';

import { SxProps, styled } from '@mui/material/styles';

import { IcClose, IcSearch } from '@/assets/images';
import { colors } from '@/theme/variables';

import { Input, InputProps } from '../FormElements/Input';

const Root = styled('div')(({ theme, style }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    height: 36,
    width: 300,
    maxWidth: style?.maxWidth ? style.maxWidth : 350,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    borderRadius: 6,
    border: '1px solid',
    borderColor: isDark ? theme.palette.divider : 'transparent',

    '& .MuiInputBase-root': {
      width: '100%',
      padding: '6px 10px',
      color: isDark ? theme.palette.text.primary : colors.grey[80],
      ...(isDark && {
        borderColor: theme.palette.divider,
        '& input::placeholder': {
          color: theme.palette.text.secondary,
          opacity: 1,
        },
      }),
    },
  };
});

interface SearchBarProps extends Omit<InputProps, 'onChange'> {
  className?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  cancelCallback?: () => void;
  onClick?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  shouldCancel?: string;
  sx?: SxProps;
}

export const SearchBar: FC<SearchBarProps> = ({
  name,
  label,
  placeholder,
  disabled,
  value,
  defaultValue = '',
  onChange,
  className,
  fullWidth,
  cancelCallback,
  shouldCancel,
  onClick,
  onFocus,
  sx,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const latestOnChangeRef = useRef(onChange);
  const lastCancelTokenRef = useRef<string | undefined>(undefined);
  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : inputValue;
  const hasDisplayValue =
    displayValue !== undefined && String(displayValue).length > 0;

  useEffect(() => {
    latestOnChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (isControlled) return;
    setInputValue(defaultValue);
  }, [defaultValue, isControlled]);

  useEffect(() => {
    if (!shouldCancel || lastCancelTokenRef.current === shouldCancel) return;

    lastCancelTokenRef.current = shouldCancel;

    if (!isControlled) {
      setInputValue('');
    }
    latestOnChangeRef.current('');
  }, [isControlled, shouldCancel]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInputValue(newValue);
    }
    onChange(newValue);
  };

  const handleCancel = () => {
    if (!isControlled) {
      setInputValue('');
    }
    onChange('');
    if (typeof cancelCallback === 'function') {
      cancelCallback();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Root className={className ?? ''} sx={sx}>
      <Input
        name={name}
        label={label}
        value={displayValue}
        placeholder={placeholder ?? 'Search'}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        disabled={disabled}
        startAdornment={<IcSearch style={{ marginRight: 4 }} />}
        endAdornment={
          hasDisplayValue && (
            <IcClose
              onClick={handleCancel}
              style={{
                cursor: 'pointer',
              }}
            />
          )
        }
        fullWidth={fullWidth}
        onClick={onClick}
        onFocus={onFocus}
      />
    </Root>
  );
};
